import ErrorStackParser from 'error-stack-parser'
import { OtelHttpJsonLogExporter } from './telemetry/otelHttpJsonLogExporter'

const TRACES_URL =
  import.meta.env.VITE_OODLE_TRACES_URL ||
  '/backend/salesforce/canvas/otel-proxy/v1/traces'
const METRICS_URL =
  import.meta.env.VITE_OODLE_METRICS_URL ||
  '/backend/salesforce/canvas/otel-proxy/v1/metrics'
const LOGS_URL =
  import.meta.env.VITE_OODLE_LOGS_URL ||
  '/backend/salesforce/canvas/otel-proxy/v1/logs'
/**
 * OTLP resource `service.name` for browser telemetry only.
 * Set `VITE_OTEL_SERVICE_NAME` or, in CI, `OTEL_SERVICE_NAME` when it ends with `-webapp-frontend`
 * (see vite.config.ts — backend `*-webapp-backend` values are ignored for the SPA).
 */
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || 'dev'
const SERVICE_NAME =
  import.meta.env.VITE_OTEL_SERVICE_NAME || `${ENVIRONMENT}-webapp-frontend`
const IS_PROD = ENVIRONMENT === 'prd'
const SAMPLING_RATE = 1

// ── Early error buffering ──
// Capture JS errors and unhandled rejections synchronously so nothing
// is lost while the OTel SDK loads asynchronously.

type BufferedError = {
  message: string
  filename: string
  lineno?: number
  colno?: number
  stack?: string
}
type BufferedRejection = { message: string; stack?: string }
type BufferedHttpError = {
  host: string
  pathname: string
  status: string
}

const errorBuffer: BufferedError[] = []
const rejectionBuffer: BufferedRejection[] = []
const httpErrorBuffer: BufferedHttpError[] = []

window.addEventListener('error', e => {
  const err = (e as ErrorEvent).error
  errorBuffer.push({
    message: (e.message || 'unknown').substring(0, 200),
    filename: e.filename || 'unknown',
    lineno:
      typeof (e as ErrorEvent).lineno === 'number'
        ? (e as ErrorEvent).lineno
        : undefined,
    colno:
      typeof (e as ErrorEvent).colno === 'number'
        ? (e as ErrorEvent).colno
        : undefined,
    stack: err instanceof Error ? err.stack : undefined,
  })
})

window.addEventListener('unhandledrejection', e => {
  const reasonValue = (e as PromiseRejectionEvent).reason
  const reason =
    reasonValue instanceof Error
      ? reasonValue.message
      : String(reasonValue ?? 'unknown')
  rejectionBuffer.push({
    message: reason.substring(0, 200),
    stack: reasonValue instanceof Error ? reasonValue.stack : undefined,
  })
})

// ─────────────────────────────────────────
// OTEL log/error helpers (best-effort, safe-by-default)
// ─────────────────────────────────────────

type ErrorKind =
  | 'window.error'
  | 'window.unhandledrejection'
  | 'react.errorboundary'
  | 'api.error'
  | 'sse.error'
  | 'manual'

type StackFrame = {
  functionName?: string
  fileName?: string
  lineNumber?: number
  columnNumber?: number
}

type SymbolicatedFrame = StackFrame & {
  originalFile?: string
  originalLine?: number
  originalColumn?: number
  originalFunction?: string
  codeFrame?: string
}

let emitOtelLog:
  | ((record: {
      severityNumber: number
      severityText: string
      body: string
      attributes?: Record<string, unknown>
      timestamp?: number
    }) => void)
  | undefined

const SOURCEMAP_LRU_MAX = 64
const SOURCEMAP_FAIL_THRESHOLD = 3

/** Minimal surface used from source-map-js consumers (keeps typing local to this module). */
type SourceMapConsumerLike = {
  originalPositionFor(pos: { line: number; column: number }): {
    source: string | null
    line: number | null
    column: number | null
    name: string | null
  }
  sourceContentFor(source: string, returnNullOnMissing: boolean): string | null
}

function createSourceMapConsumerCache() {
  const lru = new Map<string, SourceMapConsumerLike>()
  const deadUrls = new Set<string>()
  const failureStreak = new Map<string, number>()

  function putLru(key: string, consumer: SourceMapConsumerLike): void {
    lru.delete(key)
    lru.set(key, consumer)
    while (lru.size > SOURCEMAP_LRU_MAX) {
      const oldest = lru.keys().next().value
      if (oldest === undefined) break
      lru.delete(oldest)
    }
  }

  async function getSourceMapConsumer(
    jsUrl: string
  ): Promise<SourceMapConsumerLike | null> {
    if (deadUrls.has(jsUrl)) return null

    const hit = lru.get(jsUrl)
    if (hit !== undefined) {
      lru.delete(jsUrl)
      lru.set(jsUrl, hit)
      return hit
    }

    try {
      const mapUrl = `${jsUrl}.map`
      const res = await fetch(mapUrl, { credentials: 'omit' })
      if (!res.ok) throw new Error(`sourcemap_fetch_failed:${res.status}`)
      const raw = await res.json()
      const { SourceMapConsumer } = await import('source-map-js')
      const consumer = (await new SourceMapConsumer(
        raw
      )) as SourceMapConsumerLike
      failureStreak.delete(jsUrl)
      putLru(jsUrl, consumer)
      return consumer
    } catch {
      const n = (failureStreak.get(jsUrl) ?? 0) + 1
      if (n >= SOURCEMAP_FAIL_THRESHOLD) {
        deadUrls.add(jsUrl)
        failureStreak.delete(jsUrl)
      } else {
        failureStreak.set(jsUrl, n)
      }
      return null
    }
  }

  return { getSourceMapConsumer }
}

const { getSourceMapConsumer } = createSourceMapConsumerCache()

function toUnixNanoString(ms: number): string {
  // OTLP JSON expects uint64 nanoseconds, represented as a string.
  return String(BigInt(ms) * 1_000_000n)
}

function anyValueToOtlp(value: unknown): Record<string, unknown> {
  // Keep it safe: stringify unknowns to avoid schema/type issues in logs backend.
  // This also helps keep payload sizes bounded by earlier sanitization/truncation.
  if (value === null || value === undefined) return { stringValue: 'null' }
  if (typeof value === 'string') return { stringValue: value }
  if (typeof value === 'number' && Number.isFinite(value))
    return Number.isInteger(value)
      ? { intValue: value }
      : { doubleValue: value }
  if (typeof value === 'boolean') return { boolValue: value }
  try {
    return { stringValue: JSON.stringify(value) }
  } catch {
    return { stringValue: String(value) }
  }
}

async function sendOtlpLogDirect(record: {
  severityNumber: number
  severityText: string
  body: string
  attributes?: Record<string, unknown>
  timestamp?: number
}): Promise<void> {
  const timeMs = record.timestamp ?? Date.now()
  const attributes = record.attributes ?? {}

  const payload = {
    resourceLogs: [
      {
        resource: {
          attributes: [
            { key: 'service.name', value: { stringValue: SERVICE_NAME } },
            {
              key: 'deployment.environment',
              value: { stringValue: ENVIRONMENT },
            },
          ],
        },
        scopeLogs: [
          {
            scope: { name: 'frontend-telemetry' },
            logRecords: [
              {
                timeUnixNano: toUnixNanoString(timeMs),
                severityNumber: record.severityNumber,
                severityText: record.severityText,
                body: { stringValue: record.body },
                attributes: Object.entries(attributes).map(([k, v]) => ({
                  key: k,
                  value: anyValueToOtlp(v),
                })),
              },
            ],
          },
        ],
      },
    ],
  }

  try {
    const res = await fetch(LOGS_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    })
    if (!res.ok && import.meta.env.DEV) {
      console.warn(
        '[telemetry] OTLP direct log export failed:',
        res.status,
        LOGS_URL
      )
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[telemetry] OTLP direct log export error:', LOGS_URL, err)
    }
  }
}

function parseV8Stack(stack?: string): StackFrame[] {
  if (!stack) return []
  try {
    return ErrorStackParser.parse({ stack } as Error)
      .slice(0, 30)
      .map(f => ({
        functionName: f.functionName || undefined,
        fileName: f.fileName,
        lineNumber: f.lineNumber,
        columnNumber: f.columnNumber,
      }))
  } catch {
    return []
  }
}

function makeCodeFrame(source: string, line: number, col: number): string {
  const lines = source.split('\n')
  const start = Math.max(0, line - 3)
  const end = Math.min(lines.length, line + 2)
  const out: string[] = []
  for (let i = start; i < end; i++) {
    const prefix = i + 1 === line ? '>' : ' '
    const ln = String(i + 1).padStart(4, ' ')
    out.push(`${prefix} ${ln} | ${lines[i] ?? ''}`)
    if (i + 1 === line) {
      const caretPad = ' '.repeat(Math.max(0, col - 1))
      out.push(`  ${' '.repeat(4)} | ${caretPad}^`)
    }
  }
  return out.join('\n')
}

async function symbolicateFrames(
  frames: StackFrame[]
): Promise<SymbolicatedFrame[]> {
  const origin = location.origin
  const out: SymbolicatedFrame[] = []

  for (const f of frames.slice(0, 8)) {
    if (
      !f.fileName ||
      !f.lineNumber ||
      !f.columnNumber ||
      !f.fileName.startsWith(origin)
    ) {
      out.push({ ...f })
      continue
    }

    try {
      const urlNoQuery = f.fileName.split('?', 1)[0]

      // Dev (Vite): stack frames can point at /src/... or /@fs/... with real line/col.
      // In that case, fetch the source directly and build a code frame.
      const isDevSource =
        urlNoQuery.includes(`${origin}/src/`) ||
        urlNoQuery.includes(`${origin}/@fs/`)
      if (isDevSource) {
        const res = await fetch(urlNoQuery, { credentials: 'omit' })
        if (!res.ok) throw new Error(`dev_source_fetch_failed:${res.status}`)
        const text = await res.text()
        out.push({
          ...f,
          originalFile: urlNoQuery,
          originalLine: f.lineNumber,
          originalColumn: f.columnNumber,
          originalFunction: f.functionName,
          codeFrame: makeCodeFrame(text, f.lineNumber, f.columnNumber),
        })
        continue
      }

      // Production build: frames typically point at /assets/*.js; use sourcemaps.
      if (!/\/assets\/.*\.js$/.test(urlNoQuery)) {
        out.push({ ...f })
        continue
      }

      const consumer = await getSourceMapConsumer(urlNoQuery)
      if (!consumer) {
        out.push({ ...f })
        continue
      }

      const mapped = consumer.originalPositionFor({
        line: f.lineNumber,
        column: f.columnNumber,
      })

      const originalFile = mapped?.source || undefined
      const originalLine =
        typeof mapped?.line === 'number' ? mapped.line : undefined
      const originalColumn =
        typeof mapped?.column === 'number' ? mapped.column : undefined
      const originalFunction =
        typeof mapped?.name === 'string' ? mapped.name : f.functionName

      let codeFrame: string | undefined
      if (originalFile && originalLine) {
        const src = consumer.sourceContentFor(originalFile, true)
        if (typeof src === 'string') {
          codeFrame = makeCodeFrame(src, originalLine, originalColumn ?? 1)
        }
      }

      out.push({
        ...f,
        originalFile,
        originalLine,
        originalColumn,
        originalFunction,
        codeFrame,
      })
    } catch {
      out.push({ ...f })
    }
  }

  return out
}

function normalizeError(error: unknown): {
  name: string
  message: string
  stack?: string
} {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message || 'Error',
      stack: error.stack,
    }
  }
  return { name: 'Error', message: String(error ?? 'unknown') }
}

export function reportFrontendError(
  kind: ErrorKind,
  error: unknown,
  extraAttributes?: Record<string, unknown>
): void {
  const n = normalizeError(error)
  const frames = parseV8Stack(n.stack)

  // Fire-and-forget: symbolication is best-effort and async.
  void (async () => {
    const { getReduxTelemetrySnapshot } = await import(
      './store/telemetryContext'
    )
    const redux = getReduxTelemetrySnapshot()
    const symbolicated = await symbolicateFrames(frames)

    const attributes: Record<string, unknown> = {
      'event.kind': 'error',
      'error.kind': kind,
      'exception.type': n.name,
      'exception.message': n.message,
      'exception.stacktrace': n.stack,
      'error.url': location.href,
      'user.partner_id': redux?.userContext.partnerId ?? null,
      'user.partner_user_id': redux?.userContext.partnerUserId ?? null,
      'user.roles': redux ? JSON.stringify(redux.userContext.roles) : '[]',
      'user.auth_type': redux?.userContext.authType ?? 'auth0',
      ...(redux ? { 'redux.actions': JSON.stringify(redux.actions) } : {}),
      ...(extraAttributes ?? {}),
    }

    const bestFrame = symbolicated.find(f => f.codeFrame)?.codeFrame
    if (bestFrame) attributes['error.code_frame'] = bestFrame
    attributes['error.frames'] = JSON.stringify(
      symbolicated.map(f => ({
        functionName: f.functionName,
        fileName: f.fileName,
        lineNumber: f.lineNumber,
        columnNumber: f.columnNumber,
        originalFile: f.originalFile,
        originalLine: f.originalLine,
        originalColumn: f.originalColumn,
        originalFunction: f.originalFunction,
      }))
    )

    emitOtelLog?.({
      severityNumber: 17, // SeverityNumber.ERROR
      severityText: 'ERROR',
      body: `${n.name}: ${n.message}`,
      attributes,
      timestamp: Date.now(),
    })

    // Dev-safe fallback: ensure the error is shipped even if sdk-logs/exporter setup fails.
    if (!IS_PROD) {
      try {
        await sendOtlpLogDirect({
          severityNumber: 17,
          severityText: 'ERROR',
          body: `${n.name}: ${n.message}`,
          attributes: { ...attributes, 'otel.logs.transport': 'direct' },
          timestamp: Date.now(),
        })
      } catch {
        // swallow
      }
    }
  })()
}

// Wrap fetch early (before OTel instrumentations) to track HTTP errors.
// OTel's auto-instrumentation will wrap on top of this, which is fine —
// our wrapper is innermost and sees the real response/error first.
const _origFetch = window.fetch
window.fetch = function (...args: Parameters<typeof fetch>) {
  const input = args[0]
  const rawUrl =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : input.url
  let parsed: URL | undefined
  try {
    parsed = new URL(rawUrl, location.origin)
  } catch {
    /* malformed */
  }

  if (parsed && isCollectorUrl(parsed)) {
    return _origFetch.apply(this, args)
  }

  return _origFetch.apply(this, args).then(
    response => {
      if (!response.ok && parsed) {
        httpErrorBuffer.push({
          host: parsed.hostname,
          pathname: parameterizeUrl(parsed.pathname),
          status: String(response.status),
        })
      }
      return response
    },
    err => {
      if (parsed) {
        httpErrorBuffer.push({
          host: parsed.hostname,
          pathname: parameterizeUrl(parsed.pathname),
          status: 'network_error',
        })
      }
      throw err
    }
  )
}

// Track XHR errors (Axios, OTel exporters, legacy code)
const _origXHROpen = XMLHttpRequest.prototype.open
const _origXHRSend = XMLHttpRequest.prototype.send
XMLHttpRequest.prototype.open = function (
  method: string,
  url: string | URL,
  async_: boolean = true,
  username?: string | null,
  password?: string | null
) {
  ;(this as any).__otel_url = typeof url === 'string' ? url : url.href
  ;(this as any).__otel_method = method
  return _origXHROpen.call(this, method, url, async_, username, password)
}
XMLHttpRequest.prototype.send = function (
  body?: Document | XMLHttpRequestBodyInit | null
) {
  this.addEventListener('loadend', function () {
    const rawUrl = (this as any).__otel_url
    if (!rawUrl) return
    let parsed: URL | undefined
    try {
      parsed = new URL(rawUrl, location.origin)
    } catch {
      return
    }
    if (isCollectorUrl(parsed)) return
    if (this.status >= 400 || this.status === 0) {
      httpErrorBuffer.push({
        host: parsed.hostname,
        pathname: parameterizeUrl(parsed.pathname),
        status: this.status === 0 ? 'network_error' : String(this.status),
      })
    }
  })
  return _origXHRSend.call(this, body)
}

function isCollectorUrl(url: URL): boolean {
  return (
    (url.hostname === 'localhost' && url.port === '4318') ||
    url.pathname.startsWith('/backend/salesforce/canvas/otel-proxy') ||
    url.hostname.endsWith('.collector.oodle.ai') ||
    url.hostname.endsWith('.segment.io') ||
    url.hostname.endsWith('.segment.com')
  )
}

function parameterizeUrl(pathname: string): string {
  return pathname
    .replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      '{uuid}'
    )
    .replace(/\/\d+(?=\/|$)/g, '/{id}')
}

function classifyResource(name: string): string {
  if (/\.js(\?|$)/i.test(name)) return 'script'
  if (/\.css(\?|$)/i.test(name)) return 'stylesheet'
  if (/\.(png|jpe?g|gif|svg|webp|ico)(\?|$)/i.test(name)) return 'image'
  if (/\.(woff2?|ttf|otf|eot)(\?|$)/i.test(name)) return 'font'
  return 'other'
}

function detectBrowser(): {
  name: string
  version: string
  os: string
  mobile: boolean
} {
  const ua = navigator.userAgent
  let name = 'unknown'
  let version = ''

  if ((navigator as any).userAgentData) {
    const uad = (navigator as any).userAgentData
    const brand =
      uad.brands?.find(
        (b: any) => !b.brand.includes('Not') && !b.brand.includes('Chromium')
      ) || uad.brands?.[0]
    if (brand) {
      name = brand.brand
      version = brand.version
    }
    return {
      name,
      version,
      os: uad.platform || 'unknown',
      mobile: uad.mobile ?? false,
    }
  }

  // iOS browser variants use different tokens than their desktop counterparts
  if (ua.includes('FxiOS/')) {
    name = 'Firefox'
    version = ua.match(/FxiOS\/([\d.]+)/)?.[1] || ''
  } else if (ua.includes('EdgiOS/')) {
    name = 'Edge'
    version = ua.match(/EdgiOS\/([\d.]+)/)?.[1] || ''
  } else if (ua.includes('CriOS/')) {
    name = 'Chrome'
    version = ua.match(/CriOS\/([\d.]+)/)?.[1] || ''
  } else if (ua.includes('Firefox/')) {
    name = 'Firefox'
    version = ua.match(/Firefox\/([\d.]+)/)?.[1] || ''
  } else if (ua.includes('Edg/')) {
    name = 'Edge'
    version = ua.match(/Edg\/([\d.]+)/)?.[1] || ''
  } else if (ua.includes('Chrome/') && !ua.includes('Edg/')) {
    name = 'Chrome'
    version = ua.match(/Chrome\/([\d.]+)/)?.[1] || ''
  } else if (ua.includes('Safari/')) {
    name = 'Safari'
    version = ua.match(/Version\/([\d.]+)/)?.[1] || ''
  } else if (ua.includes('AppleWebKit/')) {
    // WKWebView / SFSafariViewController — no "Safari/" token present
    name = 'Safari'
    version =
      ua.match(/Version\/([\d.]+)/)?.[1] ||
      ua.match(/AppleWebKit\/([\d.]+)/)?.[1] ||
      ''
  }

  let os = 'unknown'
  if (ua.includes('Windows')) os = 'Windows'
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS'
  else if (ua.includes('Android')) os = 'Android'
  else if (ua.includes('Mac OS') || ua.includes('Macintosh')) os = 'macOS'
  else if (ua.includes('CrOS')) os = 'ChromeOS'
  else if (ua.includes('Linux')) os = 'Linux'

  return {
    name,
    version,
    os,
    mobile: /Mobi|Android|iPhone|iPad|iPod/i.test(ua),
  }
}

if (TRACES_URL && METRICS_URL) {
  Promise.all([
    import('@opentelemetry/sdk-trace-web'),
    import('@opentelemetry/sdk-trace-base'),
    import('@opentelemetry/exporter-trace-otlp-http'),
    import('@opentelemetry/instrumentation'),
    import('@opentelemetry/auto-instrumentations-web'),
    import('@opentelemetry/resources'),
    import('@opentelemetry/semantic-conventions'),
    import('@opentelemetry/sdk-metrics'),
    import('@opentelemetry/exporter-metrics-otlp-http'),
    import('@opentelemetry/sdk-logs'),
    import('@opentelemetry/api-logs'),
    import('web-vitals'),
  ])
    .then(
      ([
        { WebTracerProvider },
        { BatchSpanProcessor, ParentBasedSampler, TraceIdRatioBasedSampler },
        { OTLPTraceExporter },
        { registerInstrumentations },
        { getWebAutoInstrumentations },
        { resourceFromAttributes },
        { ATTR_SERVICE_NAME },
        { MeterProvider, PeriodicExportingMetricReader },
        { OTLPMetricExporter },
        { LoggerProvider, BatchLogRecordProcessor },
        logsApi,
        webVitals,
      ]) => {
        const browser = detectBrowser()

        const resource = resourceFromAttributes({
          [ATTR_SERVICE_NAME]: SERVICE_NAME,
          'deployment.environment': ENVIRONMENT,
          'browser.name': browser.name,
          'browser.version': browser.version,
          'os.type': browser.os,
          'browser.mobile': browser.mobile,
          'browser.language': navigator.language,
          'browser.platform': navigator.platform,
        })

        // ─────────────────────────────────────────
        // Logs
        // ─────────────────────────────────────────

        const loggerProvider = new LoggerProvider({
          resource,
          processors: [
            new BatchLogRecordProcessor(
              new OtelHttpJsonLogExporter(LOGS_URL) as any,
              {
                maxQueueSize: 200,
                scheduledDelayMillis: IS_PROD ? 5000 : 1000,
                exportTimeoutMillis: 30000,
                maxExportBatchSize: 20,
              }
            ),
          ],
        })
        logsApi.logs.setGlobalLoggerProvider(loggerProvider)

        const logger = logsApi.logs.getLogger(SERVICE_NAME)
        emitOtelLog = record => logger.emit(record as any)

        // ─────────────────────────────────────────
        // Traces
        // ─────────────────────────────────────────

        const traceExporter = new OTLPTraceExporter({
          url: TRACES_URL,
          headers: {},
        })

        const tracerProvider = new WebTracerProvider({
          resource,
          sampler: new ParentBasedSampler({
            root: new TraceIdRatioBasedSampler(SAMPLING_RATE),
          }),
          spanProcessors: [
            new BatchSpanProcessor(traceExporter, {
              maxQueueSize: 100,
              scheduledDelayMillis: 500,
              exportTimeoutMillis: 30000,
              maxExportBatchSize: 10,
            }),
          ],
        })

        tracerProvider.register()

        registerInstrumentations({
          instrumentations: [
            getWebAutoInstrumentations({
              '@opentelemetry/instrumentation-document-load': {},
              '@opentelemetry/instrumentation-fetch': {
                clearTimingResources: true,
                ignoreUrls: [
                  /localhost:4318/,
                  /\/backend\/salesforce\/canvas\/otel-proxy\//,
                  /\.collector\.oodle\.ai/,
                  /bam\.nr-data\.net/,
                  /newrelic/,
                  /NRJS-/,
                  /api\.segment\.io/,
                  /cdn\.segment\.com/,
                  /auth/,
                  /token/,
                ],
              },
              '@opentelemetry/instrumentation-xml-http-request': {
                ignoreUrls: [
                  /localhost:4318/,
                  /\/backend\/salesforce\/canvas\/otel-proxy\//,
                  /\.collector\.oodle\.ai/,
                  /bam\.nr-data\.net/,
                  /newrelic/,
                  /NRJS-/,
                  /api\.segment\.io/,
                  /cdn\.segment\.com/,
                ],
              },
              '@opentelemetry/instrumentation-user-interaction': {
                enabled: true,
              },
            }),
          ],
        })

        // ─────────────────────────────────────────
        // Metrics
        // ─────────────────────────────────────────

        const metricExporter = new OTLPMetricExporter({
          url: METRICS_URL,
          headers: {},
        })

        const meterProvider = new MeterProvider({
          resource,
          readers: [
            new PeriodicExportingMetricReader({
              exporter: metricExporter,
              exportIntervalMillis: IS_PROD ? 60_000 : 15_000,
            }),
          ],
        })

        const vitals = meterProvider.getMeter('oppsync-web-vitals')
        const app = meterProvider.getMeter('oppsync-app')

        // ── 1. Web Vitals ──

        const lcpHistogram = vitals.createHistogram('web_vitals.lcp', {
          description: 'Largest Contentful Paint',
          unit: 'ms',
        })
        const clsHistogram = vitals.createHistogram('web_vitals.cls', {
          description: 'Cumulative Layout Shift',
          unit: '1',
        })
        const inpHistogram = vitals.createHistogram('web_vitals.inp', {
          description: 'Interaction to Next Paint',
          unit: 'ms',
        })
        const fcpHistogram = vitals.createHistogram('web_vitals.fcp', {
          description: 'First Contentful Paint',
          unit: 'ms',
        })
        const ttfbHistogram = vitals.createHistogram('web_vitals.ttfb', {
          description: 'Time to First Byte',
          unit: 'ms',
        })

        webVitals.onLCP(({ value }) => lcpHistogram.record(value))
        webVitals.onCLS(({ value }) => clsHistogram.record(value))
        webVitals.onINP(({ value }) => inpHistogram.record(value))
        webVitals.onFCP(({ value }) => fcpHistogram.record(value))
        webVitals.onTTFB(({ value }) => ttfbHistogram.record(value))

        // ── 2. Page load timing ──

        const pageLoadHistogram = vitals.createHistogram('page.load_time', {
          description: 'Total page load time',
          unit: 'ms',
        })
        const domInteractiveHistogram = vitals.createHistogram(
          'page.dom_interactive',
          {
            description: 'Time until DOM is interactive',
            unit: 'ms',
          }
        )

        const recordPageLoad = () => {
          const nav = performance.getEntriesByType(
            'navigation'
          )[0] as PerformanceNavigationTiming
          if (!nav || nav.loadEventEnd === 0) return
          const routeAttrs = { url: parameterizeUrl(location.pathname) }
          pageLoadHistogram.record(nav.loadEventEnd - nav.startTime, routeAttrs)
          domInteractiveHistogram.record(
            nav.domInteractive - nav.startTime,
            routeAttrs
          )
        }

        if (document.readyState === 'complete') {
          recordPageLoad()
        } else {
          /* istanbul ignore next -- jsdom is typically "complete" */
          window.addEventListener('load', () => setTimeout(recordPageLoad, 0))
        }

        // ── 2b. Page load type (cache hit vs fresh) ──

        const pageLoadTypeCounter = vitals.createCounter('page.load_type', {
          description: 'Page load type - cache hit (revisit) or fresh load',
        })

        const visitedPages = new Set<string>()

        function recordPageLoadType() {
          const path = parameterizeUrl(location.pathname)
          const loadType = visitedPages.has(path) ? 'cache_hit' : 'fresh'
          pageLoadTypeCounter.add(1, { url: path, load_type: loadType })
          visitedPages.add(path)
        }

        if (document.readyState === 'complete') {
          recordPageLoadType()
        } else {
          /* istanbul ignore next -- jsdom is typically "complete" */
          window.addEventListener('load', recordPageLoadType)
        }

        window.addEventListener('popstate', recordPageLoadType)
        const origPushState = history.pushState
        history.pushState = function (
          ...args: Parameters<typeof history.pushState>
        ) {
          origPushState.apply(this, args)
          recordPageLoadType()
        }
        const origReplaceState = history.replaceState
        history.replaceState = function (
          ...args: Parameters<typeof history.replaceState>
        ) {
          origReplaceState.apply(this, args)
          recordPageLoadType()
        }

        // ── 3. Long task observer ──

        if ('PerformanceObserver' in window) {
          const longTaskCount = vitals.createCounter('page.long_task_count', {
            description: 'Number of long tasks (>50ms)',
          })
          const longTaskDuration = vitals.createHistogram(
            'page.long_task_duration',
            {
              description: 'Duration of long tasks',
              unit: 'ms',
            }
          )

          try {
            const observer = new PerformanceObserver(list => {
              for (const entry of list.getEntries()) {
                longTaskCount.add(1)
                longTaskDuration.record(entry.duration)
              }
            })
            observer.observe({ type: 'longtask', buffered: true })
          } catch {
            // longtask not supported
          }
        }

        // ── 4. HTTP Request Duration + 5. Throughput (single observer) ──

        const httpDuration = app.createHistogram('http.request.duration', {
          description: 'HTTP request duration by endpoint',
          unit: 'ms',
        })
        const httpRequestCount = app.createCounter('http.request.count', {
          description: 'Total HTTP requests by host and path',
        })

        if ('PerformanceObserver' in window) {
          try {
            const resourceObserver = new PerformanceObserver(list => {
              for (const entry of list.getEntries()) {
                const r = entry as PerformanceResourceTiming
                if (
                  r.initiatorType !== 'fetch' &&
                  r.initiatorType !== 'xmlhttprequest'
                )
                  continue
                const url = new URL(r.name, location.origin)
                if (isCollectorUrl(url)) continue

                const attrs = {
                  'http.host': url.hostname,
                  'http.pathname': parameterizeUrl(url.pathname),
                  'http.initiator': r.initiatorType,
                }

                const duration = r.responseEnd - r.startTime
                if (duration > 0) {
                  httpDuration.record(duration, attrs)
                }
                httpRequestCount.add(1, attrs)
              }
            })
            resourceObserver.observe({ type: 'resource', buffered: true })
          } catch {
            // resource timing not supported
          }
        }

        // ── 6. Error Rate ──
        // Counters are created here; the actual listeners were registered
        // synchronously at the top of the file and buffer into arrays.
        // We flush the buffers now and redirect future events to counters.

        const jsErrorCount = app.createCounter('app.error.js_count', {
          description: 'Unhandled JavaScript errors',
        })
        const unhandledRejectionCount = app.createCounter(
          'app.error.unhandled_rejection_count',
          {
            description: 'Unhandled promise rejections',
          }
        )
        const httpErrorCount = app.createCounter('app.error.http_count', {
          description: 'Failed network requests (4xx/5xx or network error)',
        })

        const drainBufferedErrors = () => {
          for (const e of errorBuffer.splice(0)) {
            jsErrorCount.add(1, {
              'error.message': e.message,
              'error.filename': e.filename,
            })
            reportFrontendError('window.error', new Error(e.message), {
              'error.filename': e.filename,
              'error.lineno': e.lineno,
              'error.colno': e.colno,
              'error.stack': e.stack,
            })
          }
          for (const e of rejectionBuffer.splice(0)) {
            unhandledRejectionCount.add(1, { 'error.message': e.message })
            reportFrontendError(
              'window.unhandledrejection',
              new Error(e.message),
              {
                'error.stack': e.stack,
              }
            )
          }
          for (const e of httpErrorBuffer.splice(0)) {
            httpErrorCount.add(1, {
              'http.host': e.host,
              'http.pathname': e.pathname,
              'http.status': e.status,
            })
          }
        }

        drainBufferedErrors()

        // Redirect future buffer pushes to counters via a polling drain.
        // The buffers continue to receive events from the early listeners
        // and fetch/XHR wrappers; this interval drains them into OTel.
        setInterval(drainBufferedErrors, 1000)

        // ── 7. Resource Loading Times ──

        const resourceLoadTime = app.createHistogram('resource.load_time', {
          description: 'Resource loading duration by type',
          unit: 'ms',
        })
        const resourceDnsTime = app.createHistogram('resource.dns_time', {
          description: 'DNS lookup time for resources',
          unit: 'ms',
        })
        const resourceTcpTime = app.createHistogram('resource.tcp_time', {
          description: 'TCP connection time for resources',
          unit: 'ms',
        })
        const resourceTlsTime = app.createHistogram('resource.tls_time', {
          description: 'TLS handshake time for resources',
          unit: 'ms',
        })
        const resourceTtfb = app.createHistogram('resource.ttfb', {
          description: 'Time to first byte for resources',
          unit: 'ms',
        })
        const resourceDownloadTime = app.createHistogram(
          'resource.download_time',
          {
            description: 'Content download time for resources',
            unit: 'ms',
          }
        )

        function recordInitialResources() {
          const resources = performance.getEntriesByType(
            'resource'
          ) as PerformanceResourceTiming[]
          for (const r of resources) {
            if (r.responseEnd <= 0 || r.responseEnd <= r.startTime) continue

            const url = new URL(r.name, location.origin)
            if (isCollectorUrl(url)) continue

            const type = r.initiatorType || classifyResource(r.name)
            const attrs = {
              'resource.type': type,
              'http.host': url.hostname,
            }

            resourceLoadTime.record(r.responseEnd - r.startTime, attrs)

            if (r.domainLookupEnd > 0 && r.domainLookupStart > 0) {
              resourceDnsTime.record(
                r.domainLookupEnd - r.domainLookupStart,
                attrs
              )
            }
            if (r.connectEnd > 0 && r.connectStart > 0) {
              resourceTcpTime.record(r.connectEnd - r.connectStart, attrs)
            }
            if (r.secureConnectionStart > 0 && r.connectEnd > 0) {
              resourceTlsTime.record(
                r.connectEnd - r.secureConnectionStart,
                attrs
              )
            }
            if (r.responseStart > 0 && r.requestStart > 0) {
              resourceTtfb.record(r.responseStart - r.requestStart, attrs)
            }
            if (r.responseEnd > 0 && r.responseStart > 0) {
              resourceDownloadTime.record(
                r.responseEnd - r.responseStart,
                attrs
              )
            }
          }
        }

        recordInitialResources()

        // ── 8. Active Users / Sessions ──

        const activeSessionsGauge = app.createUpDownCounter(
          'app.active_sessions',
          {
            description: 'Currently active browser sessions',
          }
        )

        activeSessionsGauge.add(1)

        // Best-effort flush on page unload. The browser may terminate before
        // the async flush completes — this is a known OTel browser limitation.
        window.addEventListener('beforeunload', () => {
          activeSessionsGauge.add(-1)
          tracerProvider.forceFlush()
          meterProvider.forceFlush()
        })

        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'hidden') {
            activeSessionsGauge.add(-1)
            tracerProvider.forceFlush()
            meterProvider.forceFlush()
          } else if (document.visibilityState === 'visible') {
            activeSessionsGauge.add(1)
          }
        })

        // ─────────────────────────────────────────

        if (!IS_PROD) {
          console.log('OpenTelemetry initialized (traces + metrics + logs)')
          console.log('Service:', SERVICE_NAME)
          console.log('Traces:', TRACES_URL)
          console.log('Metrics:', METRICS_URL)
          console.log('Logs:', LOGS_URL)
          console.log('Sampling Rate:', SAMPLING_RATE)
        }
      }
    )
    .catch(err => {
      if (!IS_PROD) console.warn('OpenTelemetry failed to initialize:', err)
    })
}

export { isCollectorUrl, parameterizeUrl, classifyResource, detectBrowser }
